import React, { useState, useCallback } from 'react';
import {
  Box,
  Alert,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { syntaxLanguageFromExtension, extensionOf } from '../../utils/previewUtils';

export interface TextPreviewPanelProps {
  content: string;
  fileName: string;
  truncated?: boolean;
  compact?: boolean;
  fontSize?: number;
  wordWrap?: boolean;
  onFontSizeChange?: (size: number) => void;
  onWordWrapChange?: (wrap: boolean) => void;
}

export const TextPreviewPanel: React.FC<TextPreviewPanelProps> = ({
  content,
  fileName,
  truncated = false,
  compact = false,
  fontSize: controlledFontSize,
  wordWrap: controlledWordWrap,
  onFontSizeChange,
  onWordWrapChange,
}) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [localFontSize, setLocalFontSize] = useState(14);
  const [localWordWrap, setLocalWordWrap] = useState(true);

  const fontSize = controlledFontSize ?? localFontSize;
  const wordWrap = controlledWordWrap ?? localWordWrap;
  const ext = extensionOf(fileName);
  const language = syntaxLanguageFromExtension(ext);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [content]);

  const setFontSize = (size: number) => {
    const next = Math.min(20, Math.max(10, size));
    if (onFontSizeChange) onFontSizeChange(next);
    else setLocalFontSize(next);
  };

  const codeStyle = theme.palette.mode === 'dark' ? oneDark : oneLight;

  return (
    <Box
      sx={{
        width: '100%',
        height: compact ? 'auto' : '100%',
        minHeight: compact ? 120 : 200,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {truncated && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Showing a preview of the file. Download to view the full content.
        </Alert>
      )}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          p: compact ? 1 : 2,
        }}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              color={copied ? 'success' : 'default'}
              aria-label="Copy content"
              sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {language ? (
          <SyntaxHighlighter
            language={language}
            style={codeStyle}
            showLineNumbers
            wrapLines={wordWrap}
            customStyle={{
              margin: 0,
              fontSize,
              borderRadius: 8,
              padding: '16px 16px 16px 0',
            }}
          >
            {content || ''}
          </SyntaxHighlighter>
        ) : (
          <Typography
            component="pre"
            sx={{
              m: 0,
              fontFamily: 'monospace',
              fontSize,
              whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
              wordBreak: wordWrap ? 'break-word' : 'normal',
              overflowX: wordWrap ? 'hidden' : 'auto',
            }}
          >
            {content || 'No content available'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TextPreviewPanel;
